import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Input } from '../components/ui'
import { signIn } from '../lib/auth-client'

export function Login() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    const trimmedEmail = email.trim()

    // Client-side validation
    if (!trimmedEmail) {
      setError('Email is required')
      return
    }

    if (!validateEmail(trimmedEmail)) {
      setError('Please enter a valid email address')
      return
    }

    if (!password) {
      setError('Password is required')
      return
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    setIsLoading(true)

    try {
      await signIn(trimmedEmail, password)
      // Invalidate auth cache so ProtectedRoute picks up the new user
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
      navigate('/')
    } catch (err) {
      if (err instanceof Error) {
        if (err.message.includes('Failed to fetch') || err.message.includes('network')) {
          setError('Connection failed. Please check your internet connection.')
        } else if (err.message.includes('401') || err.message.includes('Unauthorized')) {
          setError('Invalid email or password')
        } else if (err.message.includes('429')) {
          setError('Too many attempts. Please try again later.')
        } else {
          setError('An error occurred. Please try again.')
        }
      } else {
        setError('An unexpected error occurred')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-swiss-white">
      <div className="w-full max-w-[320px] px-4">
        {/* Logo and Title */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-8 h-8 bg-swiss-black flex items-center justify-center">
            <span className="text-swiss-white font-bold text-[14px]">D</span>
          </div>
          <span className="text-[18px] font-medium text-swiss-black">
            Derivative Admin
          </span>
        </div>

        {/* Sign In Header */}
        <h1 className="text-[11px] uppercase tracking-[0.1em] font-medium text-swiss-gray-500 mb-6">
          Sign In
        </h1>

        {/* Error Banner */}
        {error && (
          <div
            role="alert"
            aria-live="polite"
            className="mb-4 p-3 border-l-2 border-swiss-red bg-swiss-off-white"
          >
            <p className="text-[13px] text-swiss-red">{error}</p>
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
            autoComplete="email"
            disabled={isLoading}
          />

          <Input
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
            required
            autoComplete="current-password"
            disabled={isLoading}
          />

          <Button
            type="submit"
            variant="primary"
            className="w-full"
            disabled={isLoading}
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>
      </div>
    </div>
  )
}

export default Login
